/**
 * Creates a drag end handler for sortable lists
 *
 * @param {Object} params - Configuration parameters
 * @param {Array} params.items - Array of items in the current container
 * @param {string} params.type - Type of items (e.g., 'parts', 'labours', 'miscellaneous')
 * @param {string} params.itemKey - Unique identifier for the container
 * @param {Function} params.moveItem - Function to update item positions
 *
 * @returns {Function} Event handler for drag end events
 */
const createHandleDragEnd = ({ items, type, itemKey, moveItem }) => {
  return (evt) => {
    try {
      const { oldIndex, newIndex, from, to, item } = evt;

      const fromContainer = from.getAttribute('data-lineitem');
      const toContainer = to.getAttribute('data-lineitem');
      const movedItemKey = item?.dataset?.id;

      if (!movedItemKey) return;

      if (from !== to) {
        const movedItem = items.find(
          (it) => it.key.toString() === movedItemKey.toString()
        );

        if (!movedItem) return;

        moveItem(fromContainer, type, (currentList) => {
          return currentList
            .filter((it) => it.key.toString() !== movedItemKey.toString())
            .map((item, idx) => ({ ...item, position: idx }));
        });

        moveItem(toContainer, type, (currentList) => {
          const children = Array.from(to.children);
          const orderedKeys = children
            .map((child) => child.dataset?.id)
            .filter(Boolean);

          const newList = [...currentList, movedItem];

          const sortedList = orderedKeys
            .map((key) => newList.find((it) => it.key.toString() === key))
            .filter(Boolean)
            .map((item, index) => ({ ...item, position: index }));

          return sortedList;
        });
      } else {
        const indexToMove = items.findIndex(
          (it) => it.key.toString() === movedItemKey.toString()
        );
        if (indexToMove === -1) return;

        const updatedList = [...items];
        const [movedItem] = updatedList.splice(indexToMove, 1);
        updatedList.splice(newIndex, 0, movedItem);

        moveItem(
          itemKey,
          type,
          updatedList.map((item, idx) => ({ ...item, position: idx }))
        );
      }
    } catch (e) {
      console.log('[ERROR]', e);
    }
  };
};

/**
 * Creates a filter function that only allows items of a specific type to be dropped
 *
 * @param {string} expectedType - The type of items allowed (e.g., 'parts', 'labours')
 * @returns {Function} Filter function for sortable put operations
 */
const onlyAllowType = (expectedType) => {
  return (to, from, dragEl) => dragEl?.dataset?.type === expectedType;
};

export { onlyAllowType, createHandleDragEnd };
